import { useState } from 'react';
import type { SecurityFinding, SecurityReport, Severity } from '~/api/types';
import { Badge, ErrorPanel, SEVERITY_TONE, Spinner, VerdictBadge, formatDate } from '~/shared/ui';
import type { ApiError } from '~/api/client';

const SEVERITY_ORDER: readonly Severity[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];

/**
 * The security tab (§25, §43).
 *
 * Structured as a plain-language verdict first, trust signals second, and the raw finding list last
 * behind a disclosure. Leading with a table of rule identifiers would be honest but useless: almost
 * nobody can act on "SEC007", while "this application reads from an external host it did not
 * declare" is a decision someone can actually make.
 */
export function SecurityPanel({
  report,
  error,
  isLoading,
  onRetry,
}: {
  report: SecurityReport | undefined;
  error: ApiError | undefined;
  isLoading: boolean;
  onRetry: () => void;
}): JSX.Element {
  const [showFindings, setShowFindings] = useState(false);

  if (error !== undefined) return <ErrorPanel error={error} onRetry={onRetry} />;
  if (isLoading || report === undefined) {
    return (
      <div className="grid place-items-center py-12 text-slate-500">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }

  const total = SEVERITY_ORDER.reduce(
    (sum, severity) => sum + (report.countsBySeverity[severity] ?? 0),
    0,
  );

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex flex-wrap items-center gap-3">
          <VerdictBadge verdict={report.verdict} />
          {report.trust.requiresCaution ? <Badge tone="warn">Review before installing</Badge> : null}
        </div>
        <p className="mt-3 text-slate-300">{report.statement}</p>
        <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Fact label="Release" value={report.version} />
          <Fact label="Commit" value={report.commitShortSha} mono />
          <Fact label="Analyzed" value={formatDate(report.analyzedAt)} />
          <Fact label="Analyzer" value={report.analyzerVersion ?? '—'} mono />
        </dl>
      </div>

      <div>
        <h3 className="mb-3 font-semibold text-slate-100">Why you can trust this</h3>
        <ul className="space-y-2">
          {report.trust.signals.map((signal) => (
            <li
              key={signal.headline}
              className="flex items-start gap-3 rounded-lg border border-slate-800 bg-slate-900/40 p-3"
            >
              <span
                aria-hidden="true"
                className={
                  signal.tone === 'POSITIVE'
                    ? 'text-emerald-400'
                    : signal.tone === 'NEGATIVE'
                      ? 'text-red-400'
                      : 'text-amber-400'
                }
              >
                {signal.tone === 'POSITIVE' ? '✓' : '!'}
              </span>
              <div>
                <p className="text-sm font-medium text-slate-200">{signal.headline}</p>
                {signal.detail === undefined || signal.detail === '' ? null : (
                  <p className="mt-0.5 text-sm text-slate-400">{signal.detail}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h3 className="font-semibold text-slate-100">Findings</h3>
          {total === 0 ? (
            <Badge tone="good">Nothing found</Badge>
          ) : (
            SEVERITY_ORDER.filter((severity) => (report.countsBySeverity[severity] ?? 0) > 0).map(
              (severity) => (
                <Badge key={severity} tone={SEVERITY_TONE[severity]}>
                  {report.countsBySeverity[severity]} {severity.toLowerCase()}
                </Badge>
              ),
            )
          )}
        </div>

        {report.findings.length === 0 ? (
          <p className="text-sm text-slate-400">
            The automated analysis raised nothing on this release.
          </p>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setShowFindings((shown) => !shown)}
              aria-expanded={showFindings}
              className="text-sm text-brand-soft hover:underline"
            >
              {showFindings ? 'Hide' : 'Show'} the full technical report (
              {report.findings.length} {report.findings.length === 1 ? 'item' : 'items'})
            </button>
            {showFindings ? (
              <ul className="mt-3 space-y-3">
                {[...report.findings]
                  .sort(
                    (left, right) =>
                      SEVERITY_ORDER.indexOf(left.severity) - SEVERITY_ORDER.indexOf(right.severity),
                  )
                  .map((finding, index) => (
                    <FindingRow key={`${finding.ruleId}-${String(index)}`} finding={finding} />
                  ))}
              </ul>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function FindingRow({ finding }: { finding: SecurityFinding }): JSX.Element {
  return (
    <li className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={SEVERITY_TONE[finding.severity]}>{finding.severity.toLowerCase()}</Badge>
        <span className="font-mono text-xs text-slate-500">{finding.ruleId}</span>
        <span className="text-xs text-slate-500">{finding.category.toLowerCase()}</span>
      </div>
      <p className="mt-2 text-sm text-slate-200">{finding.message}</p>
      {finding.file === undefined ? null : (
        <p className="mt-1 font-mono text-xs text-slate-500">
          {finding.file}
          {finding.line === undefined ? '' : `:${String(finding.line)}`}
        </p>
      )}
      {/*
       * The excerpt is redacted server-side before it is ever sent, so a finding about a hardcoded
       * credential cannot itself leak the credential.
       */}
      {finding.excerpt === undefined ? null : (
        <pre className="mt-2 overflow-x-auto rounded bg-slate-950 p-2 font-mono text-xs text-slate-400">
          {finding.excerpt}
        </pre>
      )}
      <p className="mt-2 text-sm text-slate-400">
        <span className="text-slate-500">Recommended: </span>
        {finding.recommendation}
      </p>
    </li>
  );
}

function Fact({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex justify-between gap-3 border-b border-slate-800/60 pb-1.5">
      <dt className="text-slate-500">{label}</dt>
      <dd className={mono ? 'font-mono text-xs text-slate-300' : 'text-slate-300'}>{value}</dd>
    </div>
  );
}
